<template>
	<QuestionGroup>
		<v-row
			class="mt-4"
			dense
		>
			<v-col cols="12">
				<SSOMappingSection
					v-model="manageRolesOpen"
					:title="'Would you like to manage mapping for User Roles?'"
					:module-id="userRolesId"
					:sso-configuration="ssoConfiguration"
					:lookup-data="lookupData"
					:selected-provider="selectedProvider"
					:user-full-permissions="userFullPermissions"
					:product-roles="productRoles"
					:scim-enabled="ssoConfiguration.scimEnabled"
					:scim-groups="scimGroups"
					@update:ssoConfiguration="$emit('update:ssoConfiguration', $event)"
					@input="val => { manageRolesOpen = val; if (val) {} }"
				/>
			</v-col>
			<v-col cols="12">
				<SSOMappingSection
					v-model="manageBrandsOpen"
					:title="'Would you like to manage mapping for Brands?'"
					:module-id="brandsId"
					:sso-configuration="ssoConfiguration"
					:lookup-data="lookupData"
					:selected-provider="selectedProvider"
					:user-full-permissions="userFullPermissions"
					:product-roles="productRoles"
					:scim-enabled="ssoConfiguration.scimEnabled"
					:scim-groups="scimGroups"
					@update:ssoConfiguration="$emit('update:ssoConfiguration', $event)"
					@input="val => { manageBrandsOpen = val; if (val) {} }"
				/>
			</v-col>
			<!-- Personal Information mapping - hidden when SCIM is enabled (SCIM handles this automatically) -->
			<v-col
				v-if="!ssoConfiguration.scimEnabled"
				cols="12"
			>
				<SSOMappingSection
					v-model="managePersonalOpen"
					:title="'Would you like to manage mapping for Personal Information?'"
					:module-id="personalInfoId"
					:sso-configuration="ssoConfiguration"
					:lookup-data="lookupData"
					:selected-provider="selectedProvider"
					:user-full-permissions="userFullPermissions"
					:product-roles="productRoles"
					:scim-enabled="ssoConfiguration.scimEnabled"
					:scim-groups="scimGroups"
					@update:ssoConfiguration="$emit('update:ssoConfiguration', $event)"
					@input="val => { managePersonalOpen = val; if (val) {} }"
				/>
			</v-col>
		</v-row>
		<!-- Create User question - hidden when SCIM is enabled (SCIM handles user creation) -->
		<QuestionCard
			v-if="!ssoConfiguration.scimEnabled"
			:title="createUserQuestionTitle"
			:description="createUserQuestionDescription"
			:show-yes-no-buttons="userFullPermissions"
			:value="toggleValue"
			@input="updateCreateNewUser($event)"
		/>
	</QuestionGroup>
</template>

<script>
// @ts-nocheck
import { QuestionGroup, QuestionCard } from '../../../../components/component-library-wrapper.js'
import SSOMappingSection from './sso-mapping-section.vue'
import { getScimGroups } from '../../../../../../shared/utils/api/sso-configuration.js'
const PRODUCT_IDS = {
	ADMIN_PORTAL: 1,
	CUSTOMER_SERVICE_PORTAL: 2
}
export default {
	components: {
		QuestionGroup,
		QuestionCard,
		SSOMappingSection
	},
	props: {
		ssoConfiguration: Object,
		lookupData: Object,
		userFullPermissions: Boolean,
		productRoles: { type: Array, default: () => [] }
	},
	data () {
		return {
			showMappingModal: false,
			// Per-section toggles
			manageRolesOpen: false,
			manageBrandsOpen: false,
			managePersonalOpen: false,
			// legacy fallbacks kept for safety but dynamic IDs are used
			USER_ROLES: 1,
			BRANDS: 2,
			PERSONAL_INFO: 3,
			// SCIM Groups for dropdown
			scimGroups: []
		}
	},
	computed: {
		// Resolve component type IDs from lookup data to avoid hard-coded values
		userRolesId () {
			const ct = (this.lookupData && this.lookupData.ssoComponentTypes || []).find(x => /role/i.test(x.componentTypeName))
			return ct ? ct.id : this.USER_ROLES
		},
		brandsId () {
			const ct = (this.lookupData && this.lookupData.ssoComponentTypes || []).find(x => /brand/i.test(x.componentTypeName))
			return ct ? ct.id : this.BRANDS
		},
		personalInfoId () {
			const ct = (this.lookupData && this.lookupData.ssoComponentTypes || []).find(x => /personal\s*information/i.test(x.componentTypeName))
			return ct ? ct.id : this.PERSONAL_INFO
		},
		selectedProvider () {
			return this.lookupData.ssoProviders.find(provider => provider.id === this.ssoConfiguration.ssoProviderId)
		},
		selectedProviderName () {
			return (this.selectedProvider && this.selectedProvider.ssoProviderName) || ''
		},
		createUserQuestionTitle () {
			const productId = (this.ssoConfiguration && this.ssoConfiguration.productId)
			if (productId === PRODUCT_IDS.ADMIN_PORTAL) {
				return `Would you like to create a new Admin Portal User if one doesn't exist?`
			} else if (productId === PRODUCT_IDS.CUSTOMER_SERVICE_PORTAL) {
				return `Would you like to create a new Customer Service Portal User if one doesn't exist?`
			}
			return `Would you like to create a new User if one doesn't exist?`
		},
		userCreationProp () {
			const productId = (this.ssoConfiguration && this.ssoConfiguration.productId)
			const map = {
				[PRODUCT_IDS.ADMIN_PORTAL]: 'addAdminPortalUserIfNotExist',
				[PRODUCT_IDS.CUSTOMER_SERVICE_PORTAL]: 'addCSPortalUserIfNotExist'
			}
			return map[productId] || 'addUserIfNotExist'
		},

		toggleValue () {
			const cfg = this.ssoConfiguration || {}
			return cfg[this.userCreationProp] != null ? cfg[this.userCreationProp] : false
		},
		createUserQuestionDescription () {
			const productId = (this.ssoConfiguration && this.ssoConfiguration.productId)
			const portalTypeLabel = productId === PRODUCT_IDS.ADMIN_PORTAL
				? 'Admin Portal'
				: productId === PRODUCT_IDS.CUSTOMER_SERVICE_PORTAL
					? 'Customer Service Portal'
					: null
			const userLabel = portalTypeLabel
				? `new ${portalTypeLabel} user`
				: 'new user'
			return `When enabled, a ${userLabel} will be automatically created if no existing user is found during SSO login. If disabled, SSO login will fail for users who don't already exist in the system.`
		},
		tableHeaders () {
			const providerLabel = this.selectedProvider ? this.selectedProvider.claimMappingDisplayName : 'Provider Group'
			return [
				{
					text: 'Cassie Configuration Item',
					value: 'cassieModuleName',
					width: '50%'
				},
				{
					text: providerLabel,
					value: 'componentTypeExternalName',
					width: '50%'
				},
				{
					text: 'Action',
					value: 'action',
					width: '80px',
					align: 'center',
					sortable: false
				}
			]
		},
		inlineTableHeaders () {
			return [
				{ text: 'Mapping', value: 'mappingSummary' },
				{ text: 'Action', value: 'action', width: '120px', align: 'center', sortable: false }
			]
		}
	},
	watch: {
		'ssoConfiguration.mappedClaims': {
			immediate: false,
			deep: true,
			handler () {
				// Keep toggles in sync if data changes externally
				if (!this.manageRolesOpen) this.manageRolesOpen = this.hasExistingFor(this.userRolesId)
				if (!this.manageBrandsOpen) this.manageBrandsOpen = this.hasExistingFor(this.brandsId)
				if (!this.managePersonalOpen) this.managePersonalOpen = this.hasExistingFor(this.personalInfoId)
			}
		},
		'ssoConfiguration.scimEnabled': {
			immediate: true,
			handler (val) {
				if (val && this.ssoConfiguration?.tenantGuid) {
					this.fetchScimGroups()
				} else {
					this.scimGroups = []
				}
			}
		},
		'ssoConfiguration.tenantGuid': {
			handler (val) {
				if (val && this.ssoConfiguration?.scimEnabled) {
					this.fetchScimGroups()
				}
			}
		}
	},
	created () {
		// Open sections by default if existing mappings are present
		this.manageRolesOpen = this.hasExistingFor(this.userRolesId)
		this.manageBrandsOpen = this.hasExistingFor(this.brandsId)
		this.managePersonalOpen = this.hasExistingFor(this.personalInfoId)
	},
	methods: {
		hasExistingFor (moduleId) {
			if (moduleId == null) return false
			const list = (this.ssoConfiguration && this.ssoConfiguration.mappedClaims) || []
			const match = list.find(mc => (mc.claims && mc.claims[0] && mc.claims[0].componentTypeId) === moduleId)
			return !!(match && match.claims && match.claims.length)
		},
		getComponentTypeName (id) {
			const list = (this.lookupData && this.lookupData.ssoComponentTypes) || []
			const found = list.find(ct => ct.id === id)
			return found ? found.componentTypeName : ''
		},
		updateCreateNewUser (value) {
			if (!this.ssoConfiguration || !this.userCreationProp) return
			const updated = { ...this.ssoConfiguration, [this.userCreationProp]: value }
			this.$emit('update:ssoConfiguration', updated)
		},
		manageExistingClaims (item) {
			// Open the relevant section and load mappings for that module
			const claims = (item && item.claims) || []
			if (!claims.length) return
			const moduleId = claims[0].componentTypeId
			if (moduleId === this.userRolesId) this.manageRolesOpen = true
			else if (moduleId === this.brandsId) this.manageBrandsOpen = true
			else if (moduleId === this.personalInfoId) this.managePersonalOpen = true
		},
		getFirstClaimGroup (item) {
			const value = (item.claims[0] && item.claims[0].componentTypeExternalName)
			return value === 'personal_information' ? '' : value
		},
		deleteClaim (item) {
			this.$emit('delete-claim', item)
		},
		move (array, from, to) {
			const copy = array.slice()
			const [moved] = copy.splice(from, 1)
			copy.splice(to, 0, moved)
			return copy
		},
		async fetchScimGroups () {
			const tenantGuid = this.ssoConfiguration?.tenantGuid
			if (!tenantGuid) {
				this.scimGroups = []
				return
			}
			try {
				const { data } = await getScimGroups(tenantGuid)
				this.scimGroups = data || []
			} catch (err) {
				console.error('Failed to fetch SCIM groups:', err)
				this.scimGroups = []
			}
		}
	}
}
</script>
